import { Button } from '@mui/material';
import { useRef, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next';
import { generateNextNMonths, getEasternDateTime, timezoneDateFormatter } from '../../helpers/dateHelpers';

import './Admin.scss';
import { useAdmin } from '../../providers/Admin';
import { useEffect } from 'react';
import AddExceptions from '../../components/Admin/AddExceptions/AddExceptions';
import { exceptionColumns } from '../../constants/exceptionColumns';
import Loader from '../../components/Loader/Loader';
import Exception from '../../models/Exception';
import { useAuth } from '../../providers/Auth';
import { pushSiteInfo, registerPageInfo } from '../../helpers/analyticsHelper';
import { useProfile } from '../../providers/Profile';
import FileUpload from '../../components/Admin/FileUpload';
import { useFreezePeriod } from '../../providers/FreezePeriod';
import { FreezePeriodInterface } from '../../models/FreezePeriodInterface';
import { differenceInMonths } from 'date-fns';

export default function Admin() {
  const { t } = useTranslation();
  const adminContext = useAdmin();
  const selects = useRef([]);
  const freezePeriodContext = useFreezePeriod();
  const [nextMonths, setNextMonths] = useState<string[]>([]);
  const [openUpload, setOpenUpload] = useState(false);
  const [msg, setMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [files, setFiles] = useState([] as any);
  const [currentPairingMonthData, setCurrentPairingMonthData] = useState<FreezePeriodInterface>()
  const defaultException = useRef<Exception>({
    date: {
      pairingYear: 0,
      pairingMonth: 0
    },
    settings: {
      equipment: 'All',
      position: 'All',
      baseAirport: 'All'
    },
    selectedDates: {
      pairingPostedOn: new Date(),
      biddingClosedOn: new Date(),
      blockCloseTime: new Date(1, 10, 1, 9),
      blockCloseTZ: "EST"
    }
  });

  const [exceptions, setExceptions] = useState<Exception[]>([]);

  useEffect(() => {
    if (
      !freezePeriodContext.freezePeriodData.pairingFreeze ||
      freezePeriodContext.loading
    ) {
      return;
    }
    const currentPairingMonthData = freezePeriodContext.freezePeriodData.pairingFreeze.filter(
      (val) => val
    )[0];
    setCurrentPairingMonthData(currentPairingMonthData);
    adminContext.getPairingFrezzeData({
      year: currentPairingMonthData?.pairingYear,
      month: currentPairingMonthData?.pairingMonth
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freezePeriodContext]);

  useEffect(() => {
    if(!currentPairingMonthData) return;
    defaultException.current.date.pairingYear = currentPairingMonthData.pairingYear;
    defaultException.current.date.pairingMonth = currentPairingMonthData.pairingMonth;
    defaultException.current.selectedDates.blockCloseTime.setFullYear(currentPairingMonthData.pairingYear);
    defaultException.current.selectedDates.biddingClosedOn =
    defaultException.current.selectedDates.pairingPostedOn = new Date(
      currentPairingMonthData.pairingYear,
      currentPairingMonthData.pairingMonth - 1,
      new Date().getDate()
    );
    const currentDate = new Date();
    currentDate.setDate(1);
    currentDate.setHours(0, 0, 0, 0);
    setNextMonths(generateNextNMonths(12, differenceInMonths(new Date(currentPairingMonthData.pairingYear, currentPairingMonthData.pairingMonth - 1, 1), currentDate)).map(m => `${m.month} ${m.year}`));
  }, [currentPairingMonthData]);

  const uploadPopup = (e) => {
    // handle upload click
    setOpenUpload(true);
  }

  const addException = () => {
    setExceptions([...exceptions, defaultException.current]);
  };

  const cancelException = () => {
    setExceptions([]);
  }

  const handlePairingException = async (exception: any, index: number) => {
    const date = exception.date;
    const selectedDates = exception.selectedDates;
    const settings = {
      equipment: selects.current[`equipment-${index}`].querySelector('input').value,
      position: selects.current[`position-${index}`].querySelector('input').value,
      baseAirport: selects.current[`baseAirport-${index}`].querySelector('input').value
    };
    await adminContext.savePairingException(date, settings, selectedDates);
  }

  const submitExceptions = () => {
    exceptions.forEach(async (exception, index) => {
      await handlePairingException(exception, index);
      if (index === exceptions.length - 1) {
        adminContext.getPairingFrezzeData({ 
          year: currentPairingMonthData?.pairingYear,
          month: currentPairingMonthData?.pairingMonth
        });
      }
    });
  }

  const handleAdd = (e) => {
    setFiles([...files, ...e]);
  };

  const handleSave = () => {
    setOpenUpload(false);
    if (!files.length) {
      return;
    }
    // BE doesn’t support multiple uploads, TODO: add muti file upload from BE
    files.forEach(async (file, index) => {
     await adminContext.saveUpload(file).then (statusCode => {

          setMsg('');
          setErrorMsg('');

          switch (statusCode) {
            case 0:
              setMsg(t('Admin.upload.message'));
              break;
            case 5021:
              setErrorMsg(t('Admin.upload.errorInvalid'));
              break;
            case 5025:
              setErrorMsg(t('Admin.upload.errorCorrupt'));
              break;
            case 5026:
              setErrorMsg(t('Admin.upload.errorGeneral'));
              break;
            case 5027:
              setErrorMsg(t('Admin.upload.errorGeneral'));
              break;
            default:
              setErrorMsg(t('Admin.upload.errorGeneral'));
              break;
          }
      });

      if (index === files.length - 1) {
        adminContext.getDocumentData();
        setFiles([]);
      }
    });
  };

  const onClose = () => {
    setFiles([]);
    setOpenUpload(false);
  };

  const profileContext = useProfile();
  const user = useAuth();

  useEffect(() => {

    if (user.loading)
      return;

    if (!profileContext.profile.results) {
      return;
    }

    pushSiteInfo(user.user, profileContext.profile);

    registerPageInfo('my access app|admin', {
      "level1": "my access app",
      "level2": "admin",
      "level3": null,
      "level4": null
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profileContext.profile.results]);

  return (
    adminContext.loading || freezePeriodContext.loading ?
      <Loader /> :
      <div className='admin-container'>
        <div className='admin-header-container'>
          <div className="admin-header header-content">
            <h1>{t('Admin.title')}</h1>
          </div>
        </div>
        <div className='document-text'>
          <div className='document-content'>
            <section className="document-load-wrapper">
              <h2>{t('Admin.loadDocument')}</h2>
              <div className='document-load-container'>
              <div className="document-load">
                <Button variant="outlined" className="search-btn" onClick={(e) => uploadPopup(e)}>{t('Admin.uploadButton')}</Button>
                {msg !== '' ? <p className="message"><span className="icon-Confirmation-circle-green"></span><span  dangerouslySetInnerHTML={{ __html: msg }}></span></p> : ''}
                {errorMsg !== '' ? <p className="message error" ><span className="icon-Error-warning"></span><span  dangerouslySetInnerHTML={{ __html: errorMsg }}></span></p> : ''}
              </div>
                <div className="document-list">
                  <h3>{t('Admin.documentList')}</h3>
                  <div className="list">
                    {adminContext.documentsAndTypes ? adminContext.documentsAndTypes?.filestats?.map((f, index) => (
                      <>
                        {f.fileURL ?
                          <>
                            <span key={`confirm-${index}`} className="icon-Confirmation-circle"></span>
                            <a key={`doc-url-${index}`} href={`${f.fileURL}`}
                              aria-label={`download ${f.fileName} file`}
                              rel="noreferrer"
                              target="_blank">
                              {f.fileName}
                            </a> </> : <><span></span>
                            <span key={`file-name-${index}`}>{f.fileName}</span></>}
                        <span key={`file-time-${index}`}> {f.lastUploaded ? timezoneDateFormatter(getEasternDateTime(f.lastUploaded)) : '-'} </span>
                      </>))
                      : ''}
                  </div>
                </div>
              </div>
            </section>
            <section>
              <h2>{t('Admin.pairingFrezzeDates')} {nextMonths[0]} </h2>
              <div className='exception-table'>
                <DataGridPro
                  autoHeight
                  disableColumnMenu
                  //disableSelectionOnClick
                  rows={adminContext.pairingFreezeData.pairingFreeze}
                  hideFooterPagination
                  columns={exceptionColumns}
                  disableRowSelectionOnClick
                />
              </div>
            </section>
            {exceptions.length > 0 ?
              <section className="add-exception">
                {exceptions.map((_, index) =>
                  <AddExceptions
                    key={`add-exception-${index}`}
                    index={index}
                    selects={selects}
                    exceptions={exceptions}
                    setExceptions={setExceptions}
                  />
                )}
                <div className="btn-zone">
                  <Button variant="outlined" className="cancel-btn btn" onClick={cancelException}>{t('Admin.cancel')}</Button>
                  <Button variant="outlined" className="add-another-btn btn" onClick={addException}>{t('Admin.addAnother')}</Button>
                  <Button variant="outlined" className="submit-btn btn" onClick={submitExceptions}>{t('Admin.submit')}</Button>
                </div>
                <div className="bottom"></div>
              </section> :
              <Button variant="outlined" className="add-btn btn" onClick={addException}>{t('Admin.add.exception')}</Button>
            }
            <FileUpload handleSave={handleSave} handleAdd={handleAdd} open={openUpload} onClose={onClose} files={files} acceptedFiles={adminContext.documentsAndTypes.acceptableTypes}/>
          </div>
        </div>
      </div>
  )
}
